/**
 * Mode Selectors - Question Selection Strategies
 *
 * Mode-specific question selection logic.
 * Each selector implements a different strategy for choosing questions.
 *
 * §4.3 STABILIZATION: Extracted shared filtering helpers to reduce
 * duplication across mode selectors.
 *
 * NOTE: getWeakestFamilies and getAvailableChallengeFamilies have been
 * consolidated into masteryCanonical.ts (Step 1.5.5).
 */

import { type Question, getFamilyDefinition, TAXONOMY } from '../content'
import { AppState } from '../app/types'
import { GroupId } from '../config/systemConfig'
import {
  FluencyConfig,
  SprintConfig,
  ChallengeConfig,
  ExperimentsConfig,
} from './types'

import { createRng } from '../utils/deterministicRng'
import { getLDG, calculateFrontier } from '../governance'
import type { FamilyMasteryInput } from '../governance'
import { getFamilyMasteryKey } from '../mastery'

// ─── Shared Helpers (LL-1, LL-10) ────────────────────────────────────────────

/**
 * Build FamilyMasteryInput[] from familyMastery state for LDG frontier calculation.
 * Shared between MissionBriefing and modeSelectors to avoid duplication (LL-1).
 */
export function buildFrontierMasteryInputs(
  familyMastery: Record<string, { masteryScore: number; attempts: number }>,
  selectedGroup: GroupId,
): FamilyMasteryInput[] {
  return TAXONOMY.map((family) => {
    const key = getFamilyMasteryKey(family.familyId, selectedGroup)
    const m = familyMastery[key]
    const score = m?.masteryScore ?? 0
    return {
      familyId: family.familyId,
      masteryScore: score,
      attempts: m?.attempts ?? 0,
      isMastered: score >= 80,
    }
  })
}

/**
 * Detect Fluency cold-start: no families with 50-80% mastery for the selected group.
 * Shared between LearningLabPage (UI banner) and modeSelectors (question selection) (LL-10).
 */
export function isFluencyColdStart(
  familyMastery: Record<string, { masteryScore: number; attempts?: number }>,
  selectedGroup: GroupId,
): boolean {
  return !Object.entries(familyMastery).some(([key, m]) => {
    if (!key.endsWith(`_g${selectedGroup}`)) return false
    return m.masteryScore >= 50 && m.masteryScore <= 80
  })
}

// ─── §4.3 Shared Filtering Helpers ───────────────────────────────────────────

/**
 * Filter questions to those matching the selected group.
 * This is the fundamental group-gating filter used by all modes.
 */
function filterByGroup(
  questions: Question[],
  selectedGroup: GroupId,
): Question[] {
  return questions.filter((q) => q.group === selectedGroup)
}

/**
 * Filter questions to those within a tier range.
 */
function filterByTierRange(
  questions: Question[],
  min: number,
  max: number,
): Question[] {
  return questions.filter((q) => q.tier >= min && q.tier <= max)
}

/**
 * Filter questions to a specific family within a group.
 */
function filterByFamilyAndGroup(
  questions: Question[],
  familyId: string,
  selectedGroup: GroupId,
): Question[] {
  return questions.filter(
    (q) => q.familyId === familyId && q.group === selectedGroup,
  )
}

/**
 * Fluency Mode: Mixed practice from multiple modules
 * Focus on speed and accuracy with familiar content
 */
export function getFluencyQuestions(
  config: FluencyConfig,
  allQuestions: Question[],
  state: AppState,
  selectedGroup: GroupId,
): Question[] {
  const rng = createRng(Date.now())
  const familyMastery = state.familyMastery || {}

  // §4.3: Use shared group filter
  let candidates = filterByGroup(allQuestions, selectedGroup)

  // Module filter
  if (config.targetModules && config.targetModules.length > 0) {
    candidates = candidates.filter((q) =>
      config.targetModules!.includes(q.module),
    )
  }

  // §4.3: Use shared tier filter
  if (config.tierRange) {
    candidates = filterByTierRange(
      candidates,
      config.tierRange.min,
      config.tierRange.max,
    )
  }

  // Filter to fluency-eligible families only (exclude reading comprehension, reasoning tasks)
  const eligibleCandidates = candidates.filter((q) => {
    const family = getFamilyDefinition(q.familyId)
    return family?.fluencyEligible !== false
  })
  if (eligibleCandidates.length > 0) {
    candidates = eligibleCandidates
  }

  // Prioritize families with 50-80% mastery (fluency sweet spot)
  // NOTE: familyMastery keys include group suffix (e.g. "GET-BREUK-PLUS_g7")
  // so we must strip the suffix to match against q.familyId (e.g. "GET-BREUK-PLUS")
  const fluencyFamilies = Object.entries(familyMastery)
    .filter(([_, m]) => m.masteryScore >= 50 && m.masteryScore <= 80)
    .map(([key]) => key.replace(/_g\d+$/, ''))
  if (fluencyFamilies.length > 0) {
    const fluencyQuestions = candidates.filter((q) =>
      fluencyFamilies.includes(q.familyId),
    )
    if (fluencyQuestions.length > 0) {
      candidates = fluencyQuestions
    }
  } else {
    // ── Cold Start: Use LDG frontier for didactically appropriate starting families ──
    // When no families have 50-80% mastery (fresh student), use the LDG frontier
    // to select families that are didactically appropriate starting points,
    // rather than picking randomly from ALL eligible questions.
    try {
      const masteryInputs: FamilyMasteryInput[] = buildFrontierMasteryInputs(
        familyMastery,
        selectedGroup,
      )

      const frontierResult = calculateFrontier(getLDG(), masteryInputs, {
        maxFrontierSize: 20,
        masteryThreshold: 80,
      })

      if (frontierResult.frontier.length > 0) {
        const frontierFamilyIds = new Set(
          frontierResult.frontier.map((f) => f.familyId),
        )
        const frontierQuestions = candidates.filter((q) =>
          frontierFamilyIds.has(q.familyId),
        )
        if (frontierQuestions.length > 0) {
          candidates = frontierQuestions
        }
      }
    } catch {
      // If LDG calculation fails, fall back to all eligible candidates (existing behavior)
    }
  }

  // Estimate questions based on duration (30 seconds per question)
  const estimatedCount = Math.floor(config.duration / 30)
  const targetCount = Math.min(estimatedCount, candidates.length, 50)

  // Shuffle and select
  const shuffled = candidates.sort(() => rng.random() - 0.5)
  return shuffled.slice(0, targetCount)
}

/**
 * Sprint Mode: Focused practice on weakest family
 * Intensive drilling on a single skill
 */
export function getSprintQuestions(
  config: SprintConfig,
  allQuestions: Question[],
  state: AppState,
  selectedGroup: GroupId,
): Question[] {
  const rng = createRng(Date.now())

  // §4.3: Use shared family+group filter
  let candidates = filterByFamilyAndGroup(
    allQuestions,
    config.targetFamilyId,
    selectedGroup,
  )
  if (candidates.length === 0) {
    throw new Error(`No questions found for family ${config.targetFamilyId}`)
  }

  // If adaptive, start with easier questions
  if (config.adaptive) {
    candidates = candidates.sort((a, b) => a.tier - b.tier)
  } else {
    // Random order
    candidates = candidates.sort(() => rng.random() - 0.5)
  }

  // Warming-up: prepend 2 easy questions (tier ≤ 3) before the main set
  if (config.warmup) {
    const warmupPool = candidates.filter((q) => q.tier <= 3)
    const mainPool = candidates.filter((q) => q.tier > 3)
    const warmupQuestions = warmupPool.slice(0, Math.min(2, warmupPool.length))
    const mainQuestions = mainPool.slice(
      0,
      Math.max(0, config.targetCount - warmupQuestions.length),
    )
    return [...warmupQuestions, ...mainQuestions]
  }

  // Select target count
  return candidates.slice(0, config.targetCount)
}

/**
 * Challenge Mode: Unlock new content
 * Progressive difficulty within a family
 */
export function getChallengeQuestions(
  config: ChallengeConfig,
  allQuestions: Question[],
  state: AppState,
  selectedGroup: GroupId,
): Question[] {
  const rng = createRng(Date.now())
  // E-SCAN fix: use group-scoped key (was bare familyId → always undefined)
  const masteryKey = getFamilyMasteryKey(config.targetFamilyId, selectedGroup)
  const familyMastery = state.familyMastery?.[masteryKey]

  // §4.3: Use shared family+group filter
  let candidates = filterByFamilyAndGroup(
    allQuestions,
    config.targetFamilyId,
    selectedGroup,
  )
  if (candidates.length === 0) {
    throw new Error(`No questions found for family ${config.targetFamilyId}`)
  }

  // Determine tier range based on difficulty and current mastery
  const currentTier =
    familyMastery?.attempts > 0
      ? Math.floor(familyMastery.masteryScore / 20) + 1
      : 1
  const tierOffset = config.difficulty - 1 // 0, 1, or 2
  const minTier = Math.max(1, currentTier + tierOffset)
  const maxTier = Math.min(16, minTier + 2)

  // §4.3: Use shared tier filter
  candidates = filterByTierRange(candidates, minTier, maxTier)

  // Sort by tier (progressive difficulty)
  candidates = candidates.sort((a, b) => a.tier - b.tier)

  // Select 10-15 questions (deterministic)
  const targetCount = 10 + Math.floor(rng.random() * 6)
  return candidates.slice(0, Math.min(targetCount, candidates.length))
}

/**
 * Experiments Mode: Special question types and features
 * For testing new content or specific practice
 */
export function getExperimentsQuestions(
  config: ExperimentsConfig,
  allQuestions: Question[],
  state: AppState,
  selectedGroup: GroupId,
): Question[] {
  const rng = createRng(Date.now())

  // §4.3: Use shared group filter
  let candidates = filterByGroup(allQuestions, selectedGroup)

  // Apply experiment-specific filters
  switch (config.experimentType) {
    case 'visual_aids':
      // Questions with visual aids
      candidates = candidates.filter(
        (q) =>
          (q.stimulusAids && q.stimulusAids.length > 0) ||
          (q.supportAids && q.supportAids.length > 0),
      )
      break
    case 'interactive':
      // Interactive question types
      candidates = candidates.filter(
        (q) => q.questionType && !['multiple_choice'].includes(q.questionType),
      )
      break
    case 'new_content':
      // Recently added questions (last 30 days)
      // Show questions the user has NEVER attempted, or hasn't attempted in over 30 days
      const thirtyDaysAgo = Date.now() - 30 * 24 * 60 * 60 * 1000
      candidates = candidates.filter((q) => {
        const questionState = state.questionStates[q.id]
        // Never attempted = new content ✅
        // Last attempted MORE than 30 days ago = effectively fresh content ✅
        return !questionState || questionState.lastAttemptDate < thirtyDaysAgo
      })
      break
    case 'mixed':
    default:
      // Random selection
      break
  }

  // Shuffle and select 15-20 questions
  const shuffled = candidates.sort(() => rng.random() - 0.5)
  const targetCount = 15 + Math.floor(rng.random() * 6)
  return shuffled.slice(0, Math.min(targetCount, candidates.length))
}
